import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useApiClientUser } from '~/@api/client/useApiClientUser'
import {
  ITwoFAGeneratePurpose,
  type IUserVerifyPhoneNumberStartRequest,
  type IUserVerifyPhoneNumberVerifyRequest,
} from '~/@api/generated-api-types-user/Api'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'

export type ChangePhoneNumberStartRequest = IUserVerifyPhoneNumberStartRequest
export type ChangePhoneNumberVerifyRequest = Pick<
  IUserVerifyPhoneNumberVerifyRequest,
  'code'
>

export function useChangePhoneNumber(onPhoneNumberChanged: () => void) {
  const { setNotification } = useLocalNotifications()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [step, setStep] = useState<
    'credentials' | 'verify' | 'completed' | null
  >(null)
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string | null>(
    null
  )
  const { user } = useApiClientUser()

  const changePhoneNumberMutation = useMutation({
    mutationFn: async (props: ChangePhoneNumberStartRequest) => {
      return await user
        .twoFaCodeControllerGenerate(
          { data: props, purpose: ITwoFAGeneratePurpose.ChangePhoneNumber },
          {
            secure: true,
          }
        )
        .then((resp) => resp.data)
    },
    onSuccess: (_, variables) => {
      setVerifiedPhoneNumber(variables.number)
      setStep('verify')
    },
  })

  const {
    mutate: changePhoneNumberVerifyMutation,
    isPending: isRequestingChangePhoneNumberVerify,
    error: changePhoneNumberVerifyError,
    reset: changePhoneNumberVerifyReset,
  } = useMutation({
    mutationFn: async (props: ChangePhoneNumberVerifyRequest) => {
      if (!verifiedPhoneNumber) {
        setNotification('error', 'Telefónne číslo nie je overené')
        return
      }

      return await user.settingsControllerChangePhoneNumberVerify(
        {
          number: verifiedPhoneNumber,
          code: props.code,
        },
        { secure: true }
      )
    },
    onSuccess: () => {
      handleCloseDialog()
      onPhoneNumberChanged()
    },
  })

  function handleCloseDialog() {
    setIsDialogOpen(false)
    setStep(null)
    changePhoneNumberMutation.reset()
    changePhoneNumberVerifyReset()
  }

  function changePhoneNumber(number: { number: string }) {
    changePhoneNumberMutation.mutate(number)
  }

  return {
    step,
    setStep,
    isDialogOpen,
    handleOpenDialog: () => {
      setStep('credentials')
      setIsDialogOpen((_open) => !_open)
    },
    handleCloseDialog,
    handleReset: () => {
      changePhoneNumberMutation.reset()
      changePhoneNumberVerifyReset()
      setVerifiedPhoneNumber(null)
      setStep('credentials')
    },

    verifiedPhoneNumber,

    changePhoneNumber,
    isRequestingChangePhoneNumber: changePhoneNumberMutation.isPending,
    changePhoneNumberError:
      getApiErrorMessage(
        changePhoneNumberMutation.error,
        'Telefónne číslo sa nepodarilo zmeniť.'
      ) ?? undefined,
    changePhoneNumberCodeExpiresAt: changePhoneNumberMutation.data?.expiresAt
      ? new Date(changePhoneNumberMutation.data.expiresAt)
      : null,

    changePhoneNumberVerify: changePhoneNumberVerifyMutation,
    isRequestingChangePhoneNumberVerify,
    changePhoneNumberVerifyError: getApiErrorMessage(
      changePhoneNumberVerifyError,
      'Overenie kódu zlyhalo.'
    ),
  }
}
